<template>
  <div class="np-help" :class="'np-help--' + summary.tone">
    <button type="button" class="np-help__header" :aria-expanded="expanded.toString()" @click="toggle">
      <span class="np-help__header-left">
        <i class="fa" :class="toneIcon(summary.tone)"></i>
        <span class="np-help__title">{{ summary.text }}</span>
      </span>
      <i class="fa np-help__chevron" :class="expanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </button>

    <div v-if="expanded" class="np-help__body">
      <!-- Live status block -->
      <div class="np-help__status">
        <div class="np-help__status-row">
          <i class="fa np-help__status-icon" :class="[toneIcon(permissionStatusInfo.tone), 'tone-' + permissionStatusInfo.tone]"></i>
          <span class="np-help__status-label">Desktop notifications</span>
          <span class="np-help__status-value">{{ permissionStatusInfo.label }}</span>
        </div>
        <div class="np-help__status-row">
          <i class="fa np-help__status-icon" :class="[toneIcon(soundStatusInfo.tone), 'tone-' + soundStatusInfo.tone]"></i>
          <span class="np-help__status-label">Browser sound for this site</span>
          <span class="np-help__status-value">{{ soundStatusInfo.label }}</span>
        </div>
      </div>

      <!-- Browser picker -->
      <div class="np-help__browsers">
        <span class="np-help__browsers-label">Your browser:</span>
        <button
          v-for="b in browserTabs"
          :key="b.key"
          type="button"
          class="np-help__browser-chip"
          :class="{ 'is-active': selectedBrowser === b.key }"
          @click="selectedBrowser = b.key"
        >
          {{ b.label }}
        </button>
      </div>

      <!-- Per-browser instructions -->
      <div class="np-help__section">
        <h4 class="np-help__section-title">How to fix</h4>
        <ol class="np-help__steps" v-if="selectedBrowser === 'chrome' || selectedBrowser === 'edge'">
          <li>Click the <strong class="np-help__inline-icon">🔒</strong> <strong>lock icon</strong> (or <em>site-info</em> icon) on the left side of the address bar.</li>
          <li>Find <strong>Notifications</strong> and set it to <em>Allow</em>.</li>
          <li>Find <strong>Sound</strong> and set it to <em>Allow</em>.</li>
          <li>Reload the page.</li>
        </ol>
        <ol class="np-help__steps" v-else-if="selectedBrowser === 'firefox'">
          <li>Click the <strong class="np-help__inline-icon">🔒</strong> <strong>lock / shield icon</strong> on the left side of the address bar.</li>
          <li>Click <strong>Connection secure</strong> &rarr; <strong>More information</strong> &rarr; <strong>Permissions</strong> tab.</li>
          <li>Find <strong>Send Notifications</strong> and uncheck <em>Use Default</em>, then pick <em>Allow</em>.</li>
          <li>Firefox doesn't have a per-site <em>Sound</em> toggle — make sure your tab is not muted (right-click the tab &rarr; <em>Unmute Tab</em>).</li>
          <li>Reload the page.</li>
        </ol>
        <ol class="np-help__steps" v-else-if="selectedBrowser === 'safari'">
          <li>Open <strong>Safari</strong> menu &rarr; <strong>Settings&hellip;</strong> &rarr; <strong>Websites</strong> tab.</li>
          <li>Select <strong>Notifications</strong> in the left sidebar, find this site, set it to <em>Allow</em>.</li>
          <li>Select <strong>Auto-Play</strong> in the left sidebar, find this site, set it to <em>Allow All Auto-Play</em>.</li>
          <li>Reload the page.</li>
        </ol>
        <ol class="np-help__steps" v-else>
          <li>Open your browser's site settings (usually via a lock or site-info icon near the URL).</li>
          <li>Allow <strong>Notifications</strong> for this site.</li>
          <li>Allow <strong>Sound</strong> / <em>Auto-Play</em> for this site.</li>
          <li>Reload the page.</li>
        </ol>
      </div>

      <!-- Test buttons -->
      <div class="np-help__actions">
        <button type="button" class="btn btn-sm btn-outline-primary" @click="testNotification">
          <i class="fa fa-bell"></i> Test notification
        </button>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="testSound">
          <i class="fa fa-volume-up"></i> Test sound
        </button>
      </div>

      <!-- FYI block -->
      <div class="np-help__section np-help__section--fyi">
        <h4 class="np-help__section-title">Still not working?</h4>
        <ul class="np-help__fyi">
          <li v-if="isWindows">On Windows, check <strong>Focus Assist</strong> / Do Not Disturb — press <kbd>Win</kbd> <kbd>A</kbd> and turn off Focus.</li>
          <li v-if="isMac">On macOS, check <strong>Focus / Do Not Disturb</strong> — click the Control Center icon (top-right menu bar) and make sure Focus is off.</li>
          <li>Some browsers only show desktop notifications when the Teamio tab is <em>not</em> the active tab — try switching to another tab and testing again.</li>
          <li v-if="isWindows">In Windows Settings &rarr; System &rarr; Notifications, make sure notifications are enabled for your browser.</li>
          <li v-if="isMac">In macOS Settings &rarr; Notifications, make sure notifications are enabled for your browser.</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Push from 'push.js';
import {
  readNotificationPermission,
  hasActionableProblem,
  summarizeStatus,
  PERMISSION_STATUSES,
  SOUND_STATUSES,
} from '@/utils/notificationStatus';
import { detectSiteAudioMuted } from '@/utils/audioMuteDetection';
import { detectBrowser, BROWSERS } from '@/utils/browserDetection';

export default {
  name: 'NotificationHelp',
  props: {
    testSoundFile: { type: String, default: 'bell-123742.mp3' },
    forceExpanded: { type: Boolean, default: null },
  },
  data() {
    return {
      expanded: false,
      permission: 'unsupported',
      sound: 'unknown',
      selectedBrowser: 'other',
    };
  },
  computed: {
    summary() {
      return summarizeStatus(this.permission, this.sound);
    },
    permissionStatusInfo() {
      return PERMISSION_STATUSES[this.permission] || PERMISSION_STATUSES.unsupported;
    },
    soundStatusInfo() {
      return SOUND_STATUSES[this.sound] || SOUND_STATUSES.unknown;
    },
    browserTabs() {
      // Show the 4 real browsers; 'other' is the fallback when UA doesn't match
      return ['chrome', 'edge', 'firefox', 'safari'].map(k => BROWSERS[k]);
    },
    isWindows() {
      if (typeof navigator === 'undefined') return false;
      return /Win/i.test(navigator.platform) || /Windows/i.test(navigator.userAgent || '');
    },
    isMac() {
      if (typeof navigator === 'undefined') return false;
      return /Mac/i.test(navigator.platform) || /Macintosh/i.test(navigator.userAgent || '');
    },
  },
  created() {
    this.refreshStatus();
    this.selectedBrowser = this.autoDetectBrowser();
    if (this.forceExpanded === null) {
      this.expanded = hasActionableProblem(this.permission, this.sound);
    } else {
      this.expanded = this.forceExpanded;
    }
  },
  methods: {
    toggle() {
      this.expanded = !this.expanded;
    },

    autoDetectBrowser() {
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
      const key = detectBrowser(ua);
      // If UA doesn't match any of our 4, default the picker to chrome (most common)
      // but still let the user flip. 'other' is used internally but not shown as a tab.
      return key === 'other' ? 'chrome' : key;
    },

    refreshStatus() {
      this.permission = readNotificationPermission(typeof window !== 'undefined' ? window.Notification : undefined);
    },

    async testNotification() {
      if (this.permission === 'default') {
        try {
          Push.Permission.request(
            () => { this.permission = 'granted'; this.firePushTest(); },
            () => { this.permission = 'denied'; this.refreshStatus(); }
          );
          return;
        } catch (_) { /* fall through */ }
      }
      if (this.permission === 'denied' || this.permission === 'unsupported') {
        this.refreshStatus();
        if (this.$toast && this.$toast.warning) {
          this.$toast.warning('Notifications are blocked. Follow the steps above to allow them.');
        }
        return;
      }
      this.firePushTest();
    },

    firePushTest() {
      try {
        Push.create('Teamio test notification', {
          body: 'If you see this, desktop notifications are working.',
          timeout: 6000,
        });
      } catch (_) { /* ignore */ }
      this.refreshStatus();
    },

    async testSound() {
      try {
        const file = this.testSoundFile.startsWith('/') ? this.testSoundFile : '/sounds/' + this.testSoundFile;
        const audio = new Audio(file);
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { /* autoplay blocked */ });
        }
      } catch (_) { /* ignore */ }

      try {
        this.sound = await detectSiteAudioMuted();
      } catch (_) {
        this.sound = 'unknown';
      }
    },

    toneIcon(tone) {
      if (tone === 'ok') return 'fa-check-circle';
      if (tone === 'bad') return 'fa-times-circle';
      return 'fa-exclamation-circle';
    },
  },
};
</script>

<style lang="scss" scoped>
.np-help {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 16px;
  background: #fff;

  &--ok { border-left: 4px solid #3abf94; }
  &--warn { border-left: 4px solid #f59e0b; }
  &--bad { border-left: 4px solid #ef4444; }
}

.np-help__header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 13px;
  text-align: left;
  color: #0f172a;

  &:hover {
    background: #f8fafc;
  }

  &-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .fa {
    font-size: 16px;
  }
}

.np-help__title {
  font-weight: 600;
}

.np-help--ok .np-help__header > .np-help__header-left > .fa { color: #3abf94; }
.np-help--warn .np-help__header > .np-help__header-left > .fa { color: #f59e0b; }
.np-help--bad .np-help__header > .np-help__header-left > .fa { color: #ef4444; }

.np-help__chevron {
  color: #94a3b8;
  font-size: 12px;
}

.np-help__body {
  padding: 16px 18px 18px;
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  color: #0f172a;
}

.np-help__status {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  margin-bottom: 16px;
}

.np-help__status-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #0f172a;
  font-weight: 500;
}

.np-help__status-icon {
  font-size: 14px;
  flex-shrink: 0;

  &.tone-ok { color: #3abf94; }
  &.tone-warn { color: #f59e0b; }
  &.tone-bad { color: #ef4444; }
}

.np-help__status-label {
  flex: 1;
  font-weight: 600;
  color: #0f172a;
}

.np-help__status-value {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

.np-help__browsers {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;

  &-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    margin-right: 2px;
  }
}

.np-help__browser-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #94a3b8;
  }

  &.is-active {
    background: #3abf94;
    color: white;
    border-color: #3abf94;
    font-weight: 600;
  }
}

.np-help__section {
  margin-top: 12px;

  &--fyi {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px dashed #e2e8f0;
  }
}

.np-help__section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #334155;
  margin: 0 0 8px;
}

.np-help__steps,
.np-help__fyi {
  margin: 0;
  padding-left: 22px;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.7;

  li + li { margin-top: 6px; }

  strong { color: #0f172a; font-weight: 700; }

  em { color: #0f172a; font-style: italic; font-weight: 500; }
}

.np-help__inline-icon {
  display: inline-block;
  padding: 0 2px;
  font-size: 13px;
}

// Styled kbd — visible boxed keys (must override any parent panel kbd resets)
::v-deep kbd,
kbd {
  display: inline-block !important;
  min-width: 24px !important;
  padding: 2px 8px !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #0f172a !important;
  background: #ffffff !important;
  border: 1px solid #475569 !important;
  border-bottom-width: 2px !important;
  border-radius: 4px !important;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.1) !important;
  line-height: 1.2 !important;
  text-align: center !important;
  white-space: nowrap !important;
  margin: 0 2px !important;
  vertical-align: baseline !important;
}

.np-help__actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;

  button {
    flex: 1;
    max-width: 220px;
  }

  .fa {
    margin-right: 6px;
  }
}

@media (max-width: 600px) {
  .np-help__actions {
    flex-direction: column;

    button {
      max-width: none;
    }
  }

  .np-help__browsers {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
